import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { API_ENDPOINTS } from "../../config/api";
import { featureFlags } from "../../config/featureFlags";
import { useI18n } from "../../i18n";
import { useAuth } from "../AuthProvider";
import { Button } from "../ui/Button";
import {
  CHIP_MODES,
  chipLabelKey,
  chipQuestionKey,
  confidenceBadgeClass,
  citationDedupKey,
  dedupeCitations,
  formatCitationTitle,
  isCitationStale,
  mapInteractionError,
  mapProviderScopeLabel,
  mapRetrievalModeLabel,
  normalizeConfidenceLevel,
  truncateExcerpt,
  type InteractionCitation,
  type InteractionMode,
  type InteractionResult,
  type LoadingPhase,
} from "./governanceIntelligenceInteractionUx";

type Props = {
  intelligenceId: string;
  intelligenceType: string;
  intelligenceTitle: string;
  linkedCitationCount?: number;
};

const LOADING_PHASES: LoadingPhase[] = ["retrieving", "validating", "policy"];

function LoadingSkeleton({ label }: { label: string }) {
  return (
    <div className="space-y-2" aria-hidden="true">
      <p className="text-xs text-slate-500 m-0">{label}</p>
      <div className="animate-pulse space-y-2">
        <div className="h-3 rounded bg-slate-200/80 w-full" />
        <div className="h-3 rounded bg-slate-200/60 w-11/12" />
        <div className="h-16 rounded bg-slate-100 w-full" />
      </div>
    </div>
  );
}

const CitationCard = memo(function CitationCard({
  citation,
  staleLabel,
  pageLabel,
}: {
  citation: InteractionCitation;
  staleLabel: string;
  pageLabel: string;
}) {
  const stale = isCitationStale(citation);
  const title = formatCitationTitle(citation);
  const excerpt = truncateExcerpt(citation.excerpt);
  const confidencePct =
    citation.confidence != null && Number.isFinite(citation.confidence)
      ? `${Math.round(Math.min(1, Math.max(0, citation.confidence)) * 100)}%`
      : null;

  return (
    <li className="rounded border border-slate-100 bg-slate-50/80 px-2.5 py-2 min-w-0 break-words">
      <div className="flex flex-wrap items-center gap-1.5 text-xs font-medium text-slate-800">
        <span className="break-words">{title}</span>
        {citation.sectionTitle ? (
          <span className="text-slate-500 font-normal break-words">— {citation.sectionTitle}</span>
        ) : null}
        {citation.pageNumber != null ? (
          <span className="text-slate-500 font-normal shrink-0">{pageLabel}</span>
        ) : null}
        {stale ? (
          <span className="rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0 text-[10px] text-amber-800">
            {staleLabel}
          </span>
        ) : null}
        {confidencePct ? (
          <span className="rounded-full border border-slate-200 bg-white px-1.5 py-0 text-[10px] text-slate-600">
            {confidencePct}
          </span>
        ) : null}
      </div>
      {excerpt ? <p className="text-xs text-slate-600 m-0 mt-1 leading-relaxed">{excerpt}</p> : null}
    </li>
  );
});

function InteractionDisabledState() {
  const { t } = useI18n();
  return (
    <section
      className="rounded border border-dashed border-slate-200 bg-slate-50/80 p-3 text-sm"
      aria-label={t("complianceCenterUi.govIntelligenceInteraction.disabled.title", "AI interaction unavailable")}
      data-testid="governance-intelligence-interaction-disabled"
    >
      <div className="font-semibold text-slate-700">
        {t("complianceCenterUi.govIntelligenceInteraction.disabled.title", "AI interaction unavailable")}
      </div>
      <p className="text-xs text-slate-500 m-0 mt-1">
        {t(
          "complianceCenterUi.govIntelligenceInteraction.disabled.body",
          "Enable AI governance interaction in this environment to ask scoped questions about selected intelligence items.",
        )}
      </p>
    </section>
  );
}

export default function GovernanceIntelligenceInteractionPanel({
  intelligenceId,
  intelligenceType,
  intelligenceTitle,
  linkedCitationCount = 0,
}: Props) {
  const { t } = useI18n();
  const { token } = useAuth();
  const enabled = featureFlags.AI_GOVERNANCE_INTELLIGENCE_INTERACTION_V1;

  const [question, setQuestion] = useState("");
  const [activeMode, setActiveMode] = useState<InteractionMode>("explain");
  const [loading, setLoading] = useState(false);
  const [loadingPhase, setLoadingPhase] = useState<LoadingPhase>("retrieving");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<InteractionResult | null>(null);
  const [lastRequest, setLastRequest] = useState<{ question: string; mode: InteractionMode } | null>(null);

  useEffect(() => {
    if (!loading) return;
    let idx = 0;
    setLoadingPhase(LOADING_PHASES[0]);
    const timer = window.setInterval(() => {
      idx = (idx + 1) % LOADING_PHASES.length;
      setLoadingPhase(LOADING_PHASES[idx]);
    }, 1400);
    return () => window.clearInterval(timer);
  }, [loading]);

  const loadingMessage = useMemo(
    () =>
      t(`complianceCenterUi.govIntelligenceInteraction.loading.${loadingPhase}`, {
        defaultValue:
          loadingPhase === "validating"
            ? "Validating citations…"
            : loadingPhase === "policy"
              ? "Applying confidentiality policy…"
              : "Retrieving governance context…",
      }),
    [loadingPhase, t],
  );

  const presetChips = useMemo(
    () =>
      CHIP_MODES.map((mode) => ({
        mode,
        label: t(chipLabelKey(mode), { defaultValue: mode }),
        question: t(chipQuestionKey(mode), { defaultValue: `Scoped question for ${mode}` }),
      })),
    [t],
  );

  const ask = useCallback(
    async (nextQuestion: string, nextMode: InteractionMode) => {
      if (!token || !nextQuestion.trim() || !enabled) return;
      setLoading(true);
      setError(null);
      setQuestion(nextQuestion);
      setActiveMode(nextMode);
      setLastRequest({ question: nextQuestion.trim(), mode: nextMode });
      try {
        const res = await fetch(API_ENDPOINTS.AI_FOUNDATION_GOVERNANCE_INTELLIGENCE_ASK(intelligenceId), {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            question: nextQuestion.trim(),
            mode: nextMode,
            apiVersion: "v1",
          }),
        });
        const json = await res.json().catch(() => ({}));
        const httpStatus = res.status;
        if (!res.ok) {
          throw Object.assign(new Error(json?.message || "Interaction request failed"), { httpStatus });
        }
        setResult({
          ...(json.data as InteractionResult),
          answeredAt: new Date().toISOString(),
        });
      } catch (e) {
        const raw = e instanceof Error ? e.message : "Interaction request failed";
        const httpStatus =
          e && typeof e === "object" && "httpStatus" in e ? Number((e as { httpStatus?: number }).httpStatus) : undefined;
        setError(mapInteractionError(raw, httpStatus, t));
      } finally {
        setLoading(false);
      }
    },
    [enabled, intelligenceId, t, token],
  );

  const retry = useCallback(() => {
    if (!lastRequest) return;
    void ask(lastRequest.question, lastRequest.mode);
  }, [ask, lastRequest]);

  const clear = useCallback(() => {
    setResult(null);
    setError(null);
    setQuestion("");
    setLastRequest(null);
  }, []);

  const displayCitations = useMemo(() => dedupeCitations(result?.citations || []), [result?.citations]);
  const confidenceLevel = normalizeConfidenceLevel(result?.confidence);
  const showLocalOnly =
    String(result?.providerScope || "").toUpperCase() === "LOCAL" ||
    String(result?.retrievalMode || "").toUpperCase() === "LOCAL_RAG";
  const showRestricted =
    ["DETERMINISTIC", "BLOCKED"].includes(String(result?.providerScope || "").toUpperCase()) ||
    String(result?.retrievalMode || "").toUpperCase() === "DETERMINISTIC_ONLY";

  const pageLabel = useCallback(
    (page: string | number) =>
      t("complianceCenterUi.govIntelligenceInteraction.citations.page", {
        page: String(page),
        defaultValue: `p. ${page}`,
      }),
    [t],
  );

  if (!enabled) {
    return <InteractionDisabledState />;
  }

  return (
    <section
      className="rounded border border-indigo-200 bg-indigo-50/30 p-3 space-y-3 text-sm min-w-0"
      aria-label={t("complianceCenterUi.govIntelligenceInteraction.title", "Ask AI about this item")}
      aria-busy={loading}
      data-testid="governance-intelligence-interaction-panel"
    >
      <header className="space-y-1">
        <h3 className="font-semibold text-slate-800 m-0 text-sm">
          {t("complianceCenterUi.govIntelligenceInteraction.title", "Ask AI about this item")}
        </h3>
        <p className="text-xs text-slate-500 m-0">
          {t(
            "complianceCenterUi.govIntelligenceInteraction.subtitle",
            "Scoped governance Q&A only. No auto-remediation, tasks, or certification decisions.",
          )}
        </p>
      </header>

      <div className="flex flex-wrap gap-2 text-xs min-w-0">
        <span
          className="inline-flex max-w-full rounded-full border border-indigo-200 bg-white px-2 py-0.5 text-indigo-900 break-words"
          title={intelligenceTitle}
        >
          {t("complianceCenterUi.govIntelligenceInteraction.scopeBadge", {
            type: intelligenceType,
            title: intelligenceTitle,
            defaultValue: `Scope: ${intelligenceType} · ${intelligenceTitle}`,
          })}
        </span>
      </div>

      {!result && !loading && !error ? (
        <p className="text-xs text-slate-500 m-0" role="note">
          {linkedCitationCount === 0
            ? t(
                "complianceCenterUi.govIntelligenceInteraction.empty.noDocuments",
                "No supporting governance documents linked yet.",
              )
            : t(
                "complianceCenterUi.govIntelligenceInteraction.empty.promptHint",
                "Choose a suggested question or ask a scoped question about this item.",
              )}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2" role="group" aria-label={t("complianceCenterUi.govIntelligenceInteraction.chipsGroup", "Suggested questions")}>
        {presetChips.map((chip) => (
          <Button
            key={chip.mode}
            type="button"
            size="small"
            color={activeMode === chip.mode && loading ? "brand" : "outline"}
            disabled={loading}
            aria-pressed={activeMode === chip.mode}
            onClick={() => void ask(chip.question, chip.mode)}
          >
            {chip.label}
          </Button>
        ))}
      </div>

      <label className="block text-xs font-medium text-slate-600" htmlFor="gov-intelligence-scoped-question">
        {t("complianceCenterUi.govIntelligenceInteraction.scopedQuestionLabel", "Scoped question (optional)")}
      </label>
      <textarea
        id="gov-intelligence-scoped-question"
        className="input w-full min-h-[64px] max-h-40 resize-y"
        placeholder={t(
          "complianceCenterUi.govIntelligenceInteraction.scopedQuestionPlaceholder",
          "Ask about this item only…",
        )}
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        disabled={loading}
        aria-disabled={loading}
      />
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="small"
          color="brand"
          disabled={loading || !question.trim()}
          aria-disabled={loading || !question.trim()}
          onClick={() => void ask(question, "freeform_scoped")}
        >
          {loading
            ? t("complianceCenterUi.govIntelligenceInteraction.asking", "Asking…")
            : t("complianceCenterUi.govIntelligenceInteraction.ask", "Ask")}
        </Button>
        <Button type="button" size="small" color="outline" disabled={loading} onClick={clear}>
          {t("complianceCenterUi.govIntelligenceInteraction.clear", "Clear")}
        </Button>
      </div>

      {loading ? <LoadingSkeleton label={loadingMessage} /> : null}

      {error ? (
        <div
          className="rounded border border-red-200 bg-red-50/80 px-3 py-2 space-y-2"
          role="alert"
          aria-live="assertive"
        >
          <p className="text-xs text-red-800 m-0">{error}</p>
          {lastRequest ? (
            <Button type="button" size="small" color="outline" disabled={loading} onClick={retry}>
              {t("complianceCenterUi.govIntelligenceInteraction.retry", "Retry")}
            </Button>
          ) : null}
        </div>
      ) : null}

      {result ? (
        <div
          className={`space-y-3 rounded border bg-white p-3 min-w-0 max-h-[min(28rem,55vh)] overflow-y-auto overscroll-contain ${
            result.insufficientContext ? "border-amber-300 ring-1 ring-amber-100" : "border-slate-200"
          }`}
          aria-live="polite"
        >
          <div className="flex flex-wrap gap-1.5 text-xs" role="list" aria-label={t("complianceCenterUi.govIntelligenceInteraction.transparency", "Response metadata")}>
            {result.retrievalMode ? (
              <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5" role="listitem">
                {mapRetrievalModeLabel(result.retrievalMode, t)}
              </span>
            ) : null}
            {result.providerScope ? (
              <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5" role="listitem">
                {mapProviderScopeLabel(result.providerScope, t)}
              </span>
            ) : null}
            <span
              className={`rounded-full border px-2 py-0.5 font-medium ${confidenceBadgeClass(confidenceLevel)}`}
              role="listitem"
              title={t(
                "complianceCenterUi.govIntelligenceInteraction.confidence.tooltip",
                "Based on citation density and evidence coverage. Not a certification score.",
              )}
            >
              {t(`complianceCenterUi.govIntelligenceInteraction.confidence.${confidenceLevel}`, confidenceLevel)}
            </span>
            {showLocalOnly ? (
              <span className="rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-indigo-900" role="listitem">
                {t("complianceCenterUi.govIntelligenceInteraction.retrieval.localOnlyBadge", "Local only")}
              </span>
            ) : null}
            {showRestricted ? (
              <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-amber-900" role="listitem">
                {t("complianceCenterUi.govIntelligenceInteraction.retrieval.restrictedPolicyBadge", "Restricted policy")}
              </span>
            ) : null}
          </div>

          {result.insufficientContext ? (
            <div className="rounded border border-amber-200 bg-amber-50/60 px-2.5 py-2 space-y-1" role="status">
              <div className="text-xs font-semibold text-amber-900">
                {t("complianceCenterUi.govIntelligenceInteraction.insufficient.title", "Insufficient context")}
              </div>
              <p className="text-xs text-amber-800 m-0">
                {t(
                  "complianceCenterUi.govIntelligenceInteraction.insufficient.body",
                  "The answer cannot be fully grounded with available citations.",
                )}
              </p>
              <ul className="text-xs text-amber-800 m-0 pl-4 list-disc space-y-0.5">
                <li>{t("complianceCenterUi.govIntelligenceInteraction.insufficient.stepLinkEvidence", "Link supporting evidence")}</li>
                <li>
                  {t(
                    "complianceCenterUi.govIntelligenceInteraction.insufficient.stepUploadDocuments",
                    "Upload related governance documents",
                  )}
                </li>
                <li>
                  {t(
                    "complianceCenterUi.govIntelligenceInteraction.insufficient.stepMoreCitations",
                    "Additional citations may be required",
                  )}
                </li>
              </ul>
            </div>
          ) : null}

          <p
            className={`text-sm m-0 whitespace-pre-wrap break-words leading-relaxed ${
              result.insufficientContext ? "text-amber-950/90" : "text-slate-700"
            }`}
          >
            {result.answer || t("complianceCenterUi.govIntelligenceInteraction.noAnswer", "No answer returned.")}
          </p>

          <div className="space-y-2">
            <div className="font-semibold text-xs text-slate-700">
              {t("complianceCenterUi.govIntelligenceInteraction.citations.title", "Citations")}
              {displayCitations.length ? (
                <span className="font-normal text-slate-500"> ({displayCitations.length})</span>
              ) : null}
            </div>
            {!displayCitations.length ? (
              <p className="text-xs text-slate-500 m-0">
                {t("complianceCenterUi.govIntelligenceInteraction.empty.noCitations", "No grounded citations yet.")}
              </p>
            ) : (
              <ul className="m-0 p-0 list-none space-y-2" aria-label={t("complianceCenterUi.govIntelligenceInteraction.citations.title", "Citations")}>
                {displayCitations.slice(0, 12).map((c) => (
                  <CitationCard
                    key={citationDedupKey(c)}
                    citation={c}
                    staleLabel={t("complianceCenterUi.govIntelligenceInteraction.citations.stale", "Stale")}
                    pageLabel={
                      c.pageNumber != null
                        ? pageLabel(c.pageNumber)
                        : ""
                    }
                  />
                ))}
              </ul>
            )}
          </div>

          <footer className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-slate-500 border-t border-slate-100 pt-2">
            <span>
              {t("complianceCenterUi.govIntelligenceInteraction.audit.label", "Governed response")}
            </span>
            <span>
              {t("complianceCenterUi.govIntelligenceInteraction.audit.citationsCount", {
                count: displayCitations.length,
                defaultValue: `${displayCitations.length} citations`,
              })}
            </span>
            {result.mode ? (
              <span>
                {t("complianceCenterUi.govIntelligenceInteraction.audit.mode", {
                  mode: result.mode,
                  defaultValue: `Mode: ${result.mode}`,
                })}
              </span>
            ) : null}
            {result.answeredAt ? (
              <time dateTime={result.answeredAt}>
                {new Date(result.answeredAt).toLocaleString()}
              </time>
            ) : null}
          </footer>
        </div>
      ) : null}
    </section>
  );
}
